import { TraceXInterceptor } from '@trace-x/trace-x-generator';
import { Injectable } from '@angular/core';
import {
  GENERAL_SEPARATORS,
  HotelCriteria,
  OntologyResponse,
  SPLITTER,
} from '@surf/surf-components-core';
import { BehaviorSubject } from 'rxjs';

export type Type =
  | 'keywords'
  | 'specialRequest'
  | 'resort'
  | 'touristRegion'
  | 'hotelFacility'
  | 'roomFacility'
  | 'roomTypes'
  | 'boardBasis';

export type TypeName =
  | 'keywordsName'
  | 'specialRequestName'
  | 'resortName'
  | 'touristRegionName'
  | 'hotelFacilityName'
  | 'roomFacilityName'
  | 'roomTypesName'
  | 'boardBasisName';

export type CriteriaField = Pick<
  HotelCriteria,
  | 'keywords'
  | 'specialRequests'
  | 'resort'
  | 'touristRegion'
  | 'facilities'
  | 'roomFacilities'
  | 'roomTypes'
  | 'mealPlans'
>;

interface TypeAheadStore {
  type: Type;
  selected: Partial<OntologyResponse>;
  placeholderText: string;
  searchInitialized: boolean;
  ontologies: OntologyResponse[];
  filteredList: any[];
  URL: string;
  names: string;
}

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  private initialStore: Partial<TypeAheadStore>[] = [
    {
      type: 'keywords',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select multiple keywords',
    },
    {
      type: 'specialRequest',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select multiple special requests',
    },
    {
      type: 'resort',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select a resort type',
    },
    {
      type: 'touristRegion',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select a region',
    },
    {
      type: 'hotelFacility',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select multiple hotel facilities',
    },
    {
      type: 'roomFacility',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select multiple room facilities',
    },
    {
      type: 'roomTypes',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select multiple room types',
    },
    {
      type: 'boardBasis',
      selected: new OntologyResponse(),
      placeholderText: 'Search and select multiple board basis',
    },
  ];

  constructor() {
    this.initializeTypes(this.initialStore);
  }

  private readonly _typeAheadStore = new BehaviorSubject([]);

  readonly typeAheadStore$ = this._typeAheadStore.asObservable();

  get typeAheadStore() {
    return this._typeAheadStore.getValue();
  }

  set typeAheadStore(val) {
    this._typeAheadStore.next(val);
  }

  private initializeType(typeAhead: Partial<TypeAheadStore>, options?: Partial<Record<keyof TypeAheadStore, boolean>>) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.initializeType');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeAhead.type);

    const defaultValues: Partial<TypeAheadStore> = {
      ontologies: [],
      searchInitialized: false,
      filteredList: [],
      URL: '',
      names: '',
    };

    if (index > -1) {
      defaultValues.URL =  options?.URL ? store[index]?.URL : defaultValues.URL;

      store[index] = { ...store[index], ...typeAhead, ...defaultValues };
      this.typeAheadStore = store;
    } else {
      this.typeAheadStore = [...store, { ...typeAhead, ...defaultValues }];
    }
  }

  private initializeTypes(store: Partial<TypeAheadStore>[], options?: Partial<Record<keyof TypeAheadStore, boolean>>) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.initializeTypes');
    store.forEach((item) => {
      this.initializeType(item, options);
    });
  }

  public updateTypeAheadStore(
    typeToUpdate: Type,
    value: Partial<TypeAheadStore>
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.updateTypeAheadStore');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToUpdate);
    if (index > -1) {
      store[index] = {
        ...store[index],
        ...value,
        ontologies: [...store[index].ontologies, ...(value?.ontologies || [])],
      };
      this.typeAheadStore = store;
    }
  }

  public updateOntologiesByType(typeToUpdate: Type, ontologies: any[]) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.updateOntologiesByType');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToUpdate);
    if (index > -1) {
      store[index] = {
        ...store[index],
        ontologies: [...store[index].ontologies, ...ontologies],
      };
      this.typeAheadStore = store;
    }
  }

  public removeOntologyByType(typeToUpdate: Type, event) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.removeOntologyByType');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToUpdate);
    if (index > -1) {
      const indexOfOntologyToBeRemoved = store[index].ontologies?.findIndex(
        (ontology) => ontology.displayText === event.value
      );
      if (indexOfOntologyToBeRemoved > -1) {
        store[index]?.ontologies?.splice(indexOfOntologyToBeRemoved, 1);
        this.typeAheadStore = store;
      }

    }
  }

  public resetTypeAheadStore(optionsToKeep?: Partial<Record<keyof TypeAheadStore, boolean>>) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.resetTypeAheadStore');
    this.initializeTypes(this.initialStore, optionsToKeep);
  }

  private resetTypeAheadStoreByType(typeToReset: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.resetTypeAheadStoreByType');
    const store = this.typeAheadStore;
    const index = store.findIndex((item) => item.type === typeToReset);
    if (index > -1) {
      store[index] = this.initialStore[0];
      this.typeAheadStore = store;
    }
  }

  public getTypeAheadByType(typeToBeFound: Type): TypeAheadStore {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.getTypeAheadByType');
    return this.typeAheadStore.find((item) => item.type === typeToBeFound);
  }

  public returnedValues(
    data: any[],
    typeToBeUpdated: Type,
    criteria: HotelCriteria,
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.returnedValues');
    let filteredList = [];
    const criteriaField = this.getHotelCriteriaFieldMappedToType(typeToBeUpdated);

    if (data && data.length > 0) {
      filteredList = data;
    } else {
      const ontologies = this.getTypeAheadByType(typeToBeUpdated).ontologies;
      if (criteria && ontologies && ontologies.length === 0) {
        criteria[criteriaField] = null;
      }
    }

    this.updateTypeAheadStore(typeToBeUpdated, {
      filteredList,
    });
  }

  selectType(
    data,
    typeToBeSelected: Type,
    criteria: HotelCriteria,
    typeName?: TypeName
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.selectType');
    const type = this.getTypeAheadByType(typeToBeSelected);
    const criteriaField = this.getHotelCriteriaFieldMappedToType(typeToBeSelected);

    if (
      type.ontologies &&
      type.ontologies.filter(
        (ontology) =>
          ontology.displayText === this.getOntologyDisplayText(data, false)
      ).length === 0
    ) {
      type.ontologies.push(
        this.createOntologyData(data, type.ontologies.length, false)
      );
    }

    const typeCopy = { ...type };
    typeCopy.filteredList = [];
    typeCopy.ontologies = [];

    if (criteria && type.ontologies && type.ontologies.length > 0) {
      criteria[criteriaField] = this.createMultiSelectCriteriaString(
        type.ontologies,
        typeCopy.names,
        false
      );
      criteria[typeName] = data.name; // to be used to get display text
    }

    typeCopy.selected.displayText = data.name;
    typeCopy.searchInitialized = false;

    this.updateTypeAheadStore(typeToBeSelected, typeCopy);
  }

  public getOntologyDisplayText(data, isWithCode = false) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.getOntologyDisplayText');
    return isWithCode ? data.name + ' (' + data.code + ')' : data.name;
  }

  public createOntologyData(
    data: any,
    index: number,
    isWithCode = false
  ): OntologyResponse {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.createOntologyData');
    const res = new OntologyResponse();
    if (data) {
      res.displayText = this.getOntologyDisplayText(data, isWithCode);
      res.query = data.code;
      res.index = index;
    }
    return res;
  }

  public createMultiSelectCriteriaString(
    ontologies,
    names,
    isCommaSeparated = true
  ) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.createMultiSelectCriteriaString');
    let separator = SPLITTER.COMMA_SPLITTER;
    if (!isCommaSeparated) {
      separator = GENERAL_SEPARATORS.FIELD_SEPARATOR;
    }
    if (ontologies.length > 0) {
      let returningText = '';
      names = '';
      ontologies.forEach((ontology) => {
        if (returningText.length > 0) {
          returningText += separator;
          names += separator;
        }
        if (ontology && ontology.query) {
          returningText += ontology.query;
          names += ontology.displayText;
        } else {
          console.error('Error in setting data codes');
        }
      });
      return returningText;
    }
    return null;
  }

  public getHotelCriteriaFieldMappedToType(type: Type): keyof CriteriaField {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-hotel-criteria/src/lib/service/helper.service.ts.HelperService.getHotelCriteriaFieldMappedToType');
    let criteriaField: keyof CriteriaField;

    if (type === 'keywords') {
      criteriaField = 'keywords';
    } else if (type === 'specialRequest') {
      criteriaField = 'specialRequests';
    } else if (type === 'resort') {
      criteriaField = 'resort';
    } else if (type === 'hotelFacility') {
      criteriaField = 'facilities';
    } else if (type === 'boardBasis') {
      criteriaField = 'mealPlans';
    } else if (type === 'roomFacility') {
      criteriaField = 'roomFacilities';
    } else if (type === 'roomTypes') {
      criteriaField = 'roomTypes';
    } else if (type === 'touristRegion') {
      criteriaField = 'touristRegion';
    } else {
      throw new Error('Invalid type passed to function');
    }

    return criteriaField;
  }
}
